import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMingo } from '../context/MingoContext';
import { getPwaInstallCopy } from '../data/onboardingLocaleCopy';

const LS_KEY = 'mingo_install_banner_seen';

function isStandalone() {
  if (typeof window === 'undefined') return true;
  return (
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  );
}

function isIOSDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * 홈 화면 추가 안내 — 주황 배너, 첫 방문(또는 미설치) 시 표시, 닫기 시 로컬 저장
 */
function InstallBanner() {
  const { language } = useMingo();
  const [visible, setVisible] = useState(false);
  const pwaCopy = useMemo(() => getPwaInstallCopy(language), [language]);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(LS_KEY, '1');
    } catch {
      /* ignore */
    }
    setVisible(false);
    setDeferredPrompt(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (isStandalone()) return undefined;
    try {
      if (window.localStorage.getItem(LS_KEY) === '1') return undefined;
    } catch {
      /* ignore */
    }

    const ios = isIOSDevice();
    setIsIOS(ios);
    if (ios) {
      setVisible(true);
      return undefined;
    }

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    const tmr = window.setTimeout(() => {
      setVisible((v) => v || true);
    }, 1200);
    return () => {
      window.clearTimeout(tmr);
      window.removeEventListener('beforeinstallprompt', onBip);
    };
  }, []);

  const onInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') dismiss();
    } catch {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, dismiss]);

  if (!visible) return null;

  return (
    <div key={language} className="mingo-install-banner" role="region" aria-label={pwaCopy.bannerAria}>
      <div className="mingo-install-banner__row">
        <div className="mingo-install-banner__text-wrap">
          <p className="mingo-install-banner__title">{pwaCopy.bannerTitle}</p>
          {isIOS ? (
            <p className="mingo-install-banner__hint">{pwaCopy.iosHint}</p>
          ) : (
            <p className="mingo-install-banner__hint">{pwaCopy.androidHint}</p>
          )}
        </div>
        <div className="mingo-install-banner__actions">
          {!isIOS && deferredPrompt ? (
            <button type="button" className="mingo-install-banner__install" onClick={() => void onInstallClick()}>
              {pwaCopy.install}
            </button>
          ) : null}
          <button type="button" className="mingo-install-banner__close" onClick={dismiss} aria-label={pwaCopy.close}>
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallBanner;
